import { ContactModelV2 } from 'Models/ContactModelV2';
import { cleanPhoneNumber } from 'Modules/conversation/createConversation/components/createConversationForm/utils';
import { mapContactToPhoneNumbers } from 'Modules/softphone/resolver/phoneNumberResolver';
import type { TransferSearchResult } from 'Modules/softphone/types';

import {
  DIALABLE_CHAR_PATTERN,
  PHONE_NUMBER_PATTERN,
} from '../index.constants';

/**
 * Checks whether a query looks like a dialable phone number.
 *
 * The query must only contain digits, formatting characters (space, dash,
 * dot, parentheses) or dialable symbols (+, *, #), AND include at least one
 * dialable character — so plain formatting like "()" does not qualify.
 */
export function checkIsPhoneNumber(query: string): boolean {
  if (!query) {
    return false;
  }
  return PHONE_NUMBER_PATTERN.test(query) && DIALABLE_CHAR_PATTERN.test(query);
}

/** Returns the count of dialable characters (digits, +, *, #) in a query. */
export function getDialableLength(query: string): number {
  const matches = query.match(/[\d+*#]/g);
  return matches ? matches.length : 0;
}

/**
 * Checks if two phone numbers match by cleaning them and testing whether one
 * contains the other. Returns false if either cannot be cleaned to a usable form.
 */
export function phoneNumbersMatch(a: string, b: string): boolean {
  const cleanedA = cleanPhoneNumber(a);
  const cleanedB = cleanPhoneNumber(b);

  if (!cleanedA || !cleanedB) {
    return false;
  }
  return cleanedA.includes(cleanedB) || cleanedB.includes(cleanedA);
}

/**
 * Maps a ContactModelV2 instance to a TransferSearchResult.
 *
 * The phone numbers are pre-processed here (where we have the full contact
 * data from the FetchContactsV2 API) using the legacy transfer UX rules:
 * Office on top, deduplicated against equivalent corporate numbers, with
 * remaining distinct phones (Business, Mobile, Home, Pager, ...) listed below.
 */
export function mapContactToTransferResult(
  contact: ContactModelV2
): TransferSearchResult {
  const phoneNumbers = mapContactToPhoneNumbers(contact);

  const displayName =
    contact.name ||
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
    'Unknown';

  const contactType: 'directory' | 'external' =
    contact.source === 'Bhive' ? 'directory' : 'external';

  return {
    id: contact.id,
    sourceId: contact.sourceId,
    displayName,
    contactType,
    phoneNumbers,
    avatar: contact.avatarUrl || undefined,
    hasVoicemail: !!contact.voicemailInboxId,
    voicemailInboxId: contact.voicemailInboxId,
  };
}

/**
 * Client-side filter that ensures API results actually match the search query.
 *
 * The FetchContactsV2 API can return overly broad results when the search term
 * contains digits (it matches phone numbers loosely). This filter mirrors the
 * logic used in the "Start conversation" autocomplete (`createContactFilterOption`)
 * to ensure only relevant contacts are shown.
 */
export function matchesSearchQuery(
  result: TransferSearchResult,
  query: string
): boolean {
  if (!query) {
    return true;
  }

  const searchTerm = query.toLowerCase();

  // Text match: display name contains the search term
  if (result.displayName.toLowerCase().includes(searchTerm)) {
    return true;
  }

  // Phone number query: trust the API results (server-side matching is
  // authoritative and covers numbers not exposed in the transfer phone list).
  if (checkIsPhoneNumber(query.trim())) {
    return true;
  }

  return false;
}
